import { useEffect } from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import { Button } from './Button';
import { recordDiscovery } from '../../lib/api/discovery.gen';
import type { QuotaPeriod } from '../../lib/quotaEvents';

// QuotaTeaser (P02 §8, story 2-16) — the quota-exceeded discovery card, the
// PlanTeaser's numeric sibling. Where PlanTeaser advertises a feature the plan
// lacks (binary entitlement), QuotaTeaser renders a metered cap the org has
// hit: "N of M {metric} used this period → upgrade". Props come straight from
// the 2-15 quota_exceeded 402 body, so the card always shows the backend's
// real used/limit/period — never client-side guesses. Emits feature.discovery
// telemetry (impression on mount, cta on click) with the additive
// reason:'quota' + metric funnel fields; the CTA routes to the in-app billing
// page (plan section) unless the host overrides onUpgrade (a modal host closes
// itself first).
//
// STOCK metrics (story 2-18, e.g. `templates`): caps on how many of a thing
// EXISTS, not per-period consumption. Their 402 carries `period: null` — that
// is the signal: the copy drops "this period"/reset-date wording ("N of M in
// use"; capacity frees when one is deleted) while flow metrics keep the
// period + reset-date form.
export interface QuotaTeaserProps {
  /** Metered metric key from the quota_exceeded body, e.g. 'reportsPerMonth'. */
  metric: string;
  used: number;
  limit: number;
  period?: QuotaPeriod | null;
  /** Override the built-in upgrade navigation (default: /app/billing). */
  onUpgrade?: () => void;
  className?: string;
}

// Human labels for the seeded Plan.limits metrics (P02 §8); unknown metrics
// fall back to their key so a new cap is never rendered blank.
const METRIC_LABELS: Record<string, string> = {
  reportsPerMonth: 'reports',
  pdfExportsPerMonth: 'PDF exports',
  agentCallsPerMonth: 'agent calls',
  templates: 'templates',
  seats: 'staff seats',
};

const UPGRADE_ROUTE = '/app/billing';

export function QuotaTeaser({ metric, used, limit, period, onUpgrade, className }: QuotaTeaserProps): JSX.Element {
  const navigate = useNavigate();
  const label = METRIC_LABELS[metric] ?? metric;
  const resetsAt = period?.end ? new Date(period.end) : null;
  // period null ⇒ stock metric (2-18): no period wording, no reset date.
  const usage = period ? 'used this period' : 'in use';

  // Quota funnel impression — same feature.discovery bus as PlanTeaser (2-1),
  // distinguished by reason:'quota' + metric. Fire-and-forget telemetry.
  useEffect(() => {
    void recordDiscovery({
      featureKey: `quota.${metric}`,
      action: 'impression',
      plane: 'plan',
      reason: 'quota',
      metric,
    }).catch(() => {
      /* telemetry is best-effort */
    });
  }, [metric]);

  const upgrade = (): void => {
    void recordDiscovery({
      featureKey: `quota.${metric}`,
      action: 'cta',
      plane: 'plan',
      reason: 'quota',
      metric,
    }).catch(() => {
      /* telemetry is best-effort */
    });
    if (onUpgrade) {
      onUpgrade();
      return;
    }
    navigate(UPGRADE_ROUTE);
  };

  const fraction = limit > 0 ? Math.min(used / limit, 1) : 1;

  return (
    <div
      className={clsx('glass-card flex flex-col gap-3 p-5 text-sm', className)}
      role="group"
      aria-label={`${label} — plan limit reached`}
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <ChartBarIcon className="h-5 w-5" aria-hidden />
        {/* Story 2-147qb: converged onto the minted `tracking-eyebrow` token
            (0.3em) — was a hand-rolled `tracking-[0.2em]` doing the same
            eyebrow-label job as every other `tracking-eyebrow` consumer. */}
        <span className="text-xs font-semibold uppercase tracking-eyebrow">Plan limit</span>
      </div>
      <p className="text-base font-semibold text-foreground">
        {used} of {limit} {label} {usage}
      </p>
      <div
        role="meter"
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={Math.min(used, limit)}
        aria-valuetext={`${used} of ${limit} ${label} ${usage}`}
        aria-label={`${label} ${usage}`}
        className="h-2 w-full overflow-hidden rounded-full bg-muted"
      >
        <div
          className="h-full rounded-full bg-warning transition-all"
          style={{ width: `${fraction * 100}%` }}
        />
      </div>
      <p className="text-muted-foreground">
        You’ve reached your plan’s {label} allowance
        {resetsAt ? ` — it resets on ${resetsAt.toLocaleDateString()}` : ''}.
        {!period ? ` Deleting one of your ${label} frees a slot, or upgrade` : ' Upgrade'} for a higher limit.
      </p>
      <div>
        <Button variant="primary" onClick={upgrade}>
          Upgrade plan
        </Button>
      </div>
    </div>
  );
}
